import {Check, TimeSpan} from "@sparkware/app-utils";
import {IRealityCheckHandler} from "./reality-check-handler.interface";
import {IServiceFactoryExtended} from "../../../service-factory-extended.interface";
import {IRealityCheckPresenter} from "../presenters/reality-check-presenter.interface";
import {RealityCheckLastOccurrenceStorage} from "./reality-check-last-occurrence-storage";
import {ISessionCoolDownHandler} from "../cooldown/session-cooldown-handler.interface";
import {RealityCheckChannelMessage} from "../boradcast-channel/reality-check-broadcast-channel-messages";
import {OrbitBroadcastChannel} from "../../../../../utils/orbit-broadcast-channel";
import {NullableNumber} from "../../../../../common/types/nullable-types";
import {VoidSessionCoolDownHandler} from "../cooldown/void-session-cooldown-handler";

export interface StandardRealityCheckHandlerOptions {
    requireUserConfirmation?: boolean;
    sessionCoolDownHandler?: ISessionCoolDownHandler
}

export class StandardRealityCheckHandler implements IRealityCheckHandler {
    constructor(private readonly services: IServiceFactoryExtended,
                private readonly realityCheckPresenter: IRealityCheckPresenter,
                private readonly options?: StandardRealityCheckHandlerOptions) {

        this._lastOccurrenceStorage = new RealityCheckLastOccurrenceStorage(this.services);
        this._lastOccurrenceIndex = this._lastOccurrenceStorage.getLastOccurrenceIndex();

        this.sessionCoolDownHandler = this.options?.sessionCoolDownHandler ?? new VoidSessionCoolDownHandler();
        this._broadcastChannel = new OrbitBroadcastChannel<RealityCheckChannelMessage>('OrbitRealityCheck', this.services);
        this._broadcastChannel.subscribe(this._broadcastChannelMessageHandler);
    }



    private readonly _lastOccurrenceStorage: RealityCheckLastOccurrenceStorage;
    private _lastOccurrenceIndex: Promise<number>;
    private readonly sessionCoolDownHandler: ISessionCoolDownHandler;
    private readonly _broadcastChannel: OrbitBroadcastChannel<RealityCheckChannelMessage>;

    private _isDisposed = false;
    dispose(): void {
        this._isDisposed = true;
        this._broadcastChannel.close();
        this.realityCheckPresenter.dispose();
        this.sessionCoolDownHandler.dispose();
        this._lastOccurrenceStorage.dispose();
    }

    async sendUniqueAnalyticsEvent(eventKey: string, sendEvent: (businessCorrelationId: string) => Promise<void>): Promise<void> {
        await this._lastOccurrenceStorage.sendAnalyticsEvent(eventKey, sendEvent);
    }

    async getBusinessCorrelationId(): Promise<string> {
        return await this._lastOccurrenceStorage.getBusinessCorrelationId();
    }

    private _broadcastChannelMessageHandler = (message: RealityCheckChannelMessage) => {
        switch (message.type) {
            case "rc-dialog-confirmed":
                this.realityCheckPresenter.forceClose();
                break;
            /*
            case 'other-message':
                console.log(event.data.payload.someData);
                break;
             */
        }
    }

    private _isHandleRealityCheckInProgress = false
    async handleRealityCheck(sessionTimer: TimeSpan): Promise<void> {
        if(this._isDisposed) {
            return;
        }

        const newOccurrenceIndex = await this._computeNewOccurrenceIndex(sessionTimer);

        if(Check.isNullOrUndefined(newOccurrenceIndex)) {
            return;
        }

        let isLockAcquired: boolean = false;
        const lastIndex = await this._lastOccurrenceIndex;
        if(newOccurrenceIndex === lastIndex) {
            if(this.options?.requireUserConfirmation) {
                if(await this._lastOccurrenceStorage.isConfirmedByUser()) {
                    this.sessionCoolDownHandler.continueCoolDown();
                    return;
                }
            } else {
                return;
            }
        } else {
            this._lastOccurrenceIndex = Promise.resolve(newOccurrenceIndex);
            isLockAcquired = await this._lastOccurrenceStorage.setNewOccurrenceIndex(newOccurrenceIndex);
        }

        if(isLockAcquired) {
            //The restriction here is for the case when the user try to bypass the cool down by removing the reality check dialog from DOM
            //we also apply restriction only if the lock is acquired on setNewOccurrenceIndex otherwise the restriction will be applied from all open tabs
            await this.sessionCoolDownHandler.applyRestriction();
        }

        if(this._isHandleRealityCheckInProgress) {
            //it means that there is already an open reality check waiting for the user to confirm it
            return;
        }

        this._isHandleRealityCheckInProgress = true;
        try {

            const {isForceClosed} = await this.realityCheckPresenter.showRealityCheck({
                analytics: this
            });

            await this._executeActionsAfterRealityCheckMessageIsConfirmed({
                isForceClosed: isForceClosed
            });

        } finally {
            this._isHandleRealityCheckInProgress = false;
        }
    }

    private async _computeNewOccurrenceIndex(sessionTimer: TimeSpan): Promise<NullableNumber> {
        const gameRunner = await this.services.gameRunner;
        if (!gameRunner.shouldShowRealityCheckMessage) {
            return null;
        }

        const realityCheckInterval = this.services.regulation.current.getRealityCheckInterval();
        if(realityCheckInterval.totalMilliseconds <= 0) {
            return null;
        }

        //console.log(`SessionTimer: ${sessionTimer.totalMilliseconds} | RC: ${realityCheckInterval.totalMilliseconds}`);

        if(sessionTimer.totalMilliseconds < realityCheckInterval.totalMilliseconds) {
            return null;
        }

        const newOccurrenceIndex = Math.floor(sessionTimer.totalMilliseconds / realityCheckInterval.totalMilliseconds);

        const lastIndex = await this._lastOccurrenceIndex;

        if(newOccurrenceIndex < lastIndex) {
            return null;
        }

        return newOccurrenceIndex;
    }

    private async _executeActionsAfterRealityCheckMessageIsConfirmed(params: {isForceClosed: boolean}): Promise<void> {
        if(this._isDisposed) {
            return;
        }

        if(!params.isForceClosed) { //if isForceClosed would be true it means that the RC dialog was closed and confirmed from another tab.
            await this._lastOccurrenceStorage.confirm();

            this._broadcastChannel.sendMessage({
                type: "rc-dialog-confirmed",
                payload: {
                    sourceBrowserTabId: this.services.application.currentBrowserTabId
                }
            });

            //We apply again the restriction because the restriction must start from the moment reality check is confirmed by the user
            this.sessionCoolDownHandler.applyRestriction(); //no need to await for this one.
        }

        this.sessionCoolDownHandler.showCoolDown();
    }

}